/**
 * Polymorphic localized value: either a plain string (legacy) or a per-language map.
 *
 * - `string`           → legacy value, used as-is for every language.
 * - `{ es?, en?, fr? }` → translation map. Falls back to `fallbackLanguage` (default `es`)
 *                         and finally to any first available value.
 *
 * Supported languages are kept aligned with `src/i18n/locales/` (es, en, fr).
 */

export type SupportedLanguage = 'es' | 'en' | 'fr';

export type LocalizedMap = Partial<Record<SupportedLanguage, string>>;

export type Localized = string | LocalizedMap | undefined | null;

const DEFAULT_FALLBACK_LANGUAGE: SupportedLanguage = 'es';

const isLocalizedMap = (value: unknown): value is LocalizedMap =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const normalizeLanguage = (language?: string): SupportedLanguage => {
  if (!language) return DEFAULT_FALLBACK_LANGUAGE;
  const short = language.slice(0, 2).toLowerCase() as SupportedLanguage;
  return (['es', 'en', 'fr'] as SupportedLanguage[]).includes(short)
    ? short
    : DEFAULT_FALLBACK_LANGUAGE;
};

/**
 * Resolve a `Localized` value into a plain string for the requested language.
 *
 * @example
 *   getLocalizedValue('Title', 'en')                              // 'Title'
 *   getLocalizedValue({ es: 'Hola', en: 'Hi' }, 'en')             // 'Hi'
 *   getLocalizedValue({ es: 'Hola' }, 'fr')                       // 'Hola' (fallback)
 *   getLocalizedValue(undefined, 'es')                            // ''
 */
export const getLocalizedValue = (
  value: Localized,
  language?: string,
  fallbackLanguage: SupportedLanguage = DEFAULT_FALLBACK_LANGUAGE
): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;

  if (!isLocalizedMap(value)) return '';

  const lang = normalizeLanguage(language);
  if (value[lang]) return value[lang] as string;
  if (value[fallbackLanguage]) return value[fallbackLanguage] as string;

  const firstAvailable = Object.values(value).find(
    (v): v is string => typeof v === 'string' && v.length > 0
  );
  return firstAvailable || '';
};

/**
 * Update a `Localized` value while preserving its original shape:
 *
 * - If `previous` is a string (or empty), returns a string (legacy mode preserved).
 * - If `previous` is a `{ es?, en?, fr? }` map, returns the same map with the
 *   requested language updated (other languages untouched).
 *
 * Use this in form editors so a user editing the current language does not
 * accidentally drop the translations stored in other languages.
 *
 * @example
 *   setLocalizedValue('Old', 'New', 'es')                  // 'New'
 *   setLocalizedValue({ es: 'Hola', en: 'Hi' }, 'Bonjour', 'fr')
 *   //                                                    // { es: 'Hola', en: 'Hi', fr: 'Bonjour' }
 */
export const setLocalizedValue = (
  previous: Localized,
  newValue: string,
  language?: string
): Localized => {
  const lang = normalizeLanguage(language);

  if (isLocalizedMap(previous)) {
    return { ...previous, [lang]: newValue };
  }

  return newValue;
};
